import {
  IconPlayerPlayFilled,
  IconPlayerStopFilled,
} from "@tabler/icons-react";
import * as turf from "@turf/turf";
import chroma from "chroma-js";
import { useAtomValue } from "jotai";
import { useMemo } from "react";
import { Marker } from "react-map-gl";
import { mapInstanceAtom, mapViewStateAtom } from "~/stores";

interface BoundaryLabel {
  text: string;
  icon?: React.ReactNode;
  position: GeoJSON.Position;
  offset: [number, number];
}

const MIN_PIXEL_DISTANCE = 50;
const MIN_POLYGON_PIXEL_AREA = 2500;

export function BoundaryLabel({
  feature,
}: {
  feature: GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>;
}) {
  const map = useAtomValue(mapInstanceAtom);
  if (!map) return null;

  // console.log(feature);

  if (feature.geometry.type === "LineString") {
    return (
      <LineStringLabel
        feature={
          feature as GeoJSON.Feature<
            GeoJSON.LineString,
            GeoJSON.GeoJsonProperties
          >
        }
      />
    );
  } else if (feature.geometry.type === "Polygon") {
    return (
      <PolygonLabel
        feature={
          feature as GeoJSON.Feature<GeoJSON.Polygon, GeoJSON.GeoJsonProperties>
        }
      />
    );
  }

  //

  return null;
}

function PolygonLabel({
  feature,
}: {
  feature: GeoJSON.Feature<GeoJSON.Polygon, GeoJSON.GeoJsonProperties>;
}) {
  const map = useAtomValue(mapInstanceAtom);
  const viewState = useAtomValue(mapViewStateAtom);

  const bgColor = chroma(feature.properties?.fillColor ?? "#ffffff");
  const textColor = bgColor.luminance() > 0.5 ? "black" : "white";

  const labels: BoundaryLabel[] = useMemo(() => {
    if (!map) return [];
    const polygon = turf.polygon(feature.geometry.coordinates);
    const bbox = turf.bbox(polygon);
    const center = turf.centroid(polygon);
    const topLeft = map.project([bbox[0], bbox[3]]);
    const bottomRight = map.project([bbox[2], bbox[1]]);
    const pixelArea = Math.abs(
      (bottomRight.x - topLeft.x) * (bottomRight.y - topLeft.y)
    );
    if (pixelArea <= MIN_POLYGON_PIXEL_AREA) {
      return [];
    }
    let text = feature.properties?.label;
    if (feature.properties?.isCircle) {
      const distanceInMiles = turf.distance(
        turf.point(center.geometry.coordinates),
        turf.point(feature.geometry.coordinates[0][0]),
        { units: "miles" }
      );
      let radius, unit;
      if (distanceInMiles < 0.1) {
        radius = (distanceInMiles * 5280).toFixed(0);
        unit = "ft";
      } else {
        radius = distanceInMiles.toFixed(2);
        unit = "mi";
      }
      text = `${radius} ${unit}`;
    } else {
      const areaInSqMeters = turf.area(feature);
      if (areaInSqMeters < 929) {
        // Less than 10,000 sq ft
        const sqFt = areaInSqMeters * 10.7639;
        text = `${sqFt.toFixed(0)} sq ft`;
      } else if (areaInSqMeters < 4046856) {
        // Less than 1000 acres
        const acres = areaInSqMeters / 4046.86;
        text = `${acres.toFixed(2)} acres`;
      } else {
        const sqMiles = areaInSqMeters / 2589988.11;
        text = `${sqMiles.toFixed(2)} sq mi`;
      }
    }

    const displayLabels = feature.properties?.displayLabels === "on";
    const displayRadius = feature.properties?.displayRadius === "on";
    const displayArea = feature.properties?.displayArea === "on";
    const shift = displayArea || displayRadius;
    return [
      ...(displayLabels
        ? [
            {
              text: feature.properties?.label,
              position: center.geometry.coordinates,
              offset: [0, shift ? -15 : 0] as [number, number],
            },
          ]
        : []),
      ...(displayRadius || displayArea
        ? [
            {
              text,
              position: center.geometry.coordinates,
              offset: [0, displayLabels ? 15 : 0] as [number, number],
            },
          ]
        : []),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, feature, viewState.zoom]);

  return (
    <>
      {labels.map((label, index) => (
        <Marker
          key={index}
          longitude={label.position[0]}
          latitude={label.position[1]}
          offset={label.offset}
        >
          <div
            className="px-2 py-0.5 rounded-full font-medium flex items-center gap-1"
            style={
              {
                backgroundColor: feature.properties?.fillColor ?? "white",
                color: textColor,
              } as React.CSSProperties
            }
          >
            {label.icon}
            {label.text}
          </div>
        </Marker>
      ))}
    </>
  );
}

function LineStringLabel({
  feature,
}: {
  feature: GeoJSON.Feature<GeoJSON.LineString, GeoJSON.GeoJsonProperties>;
}) {
  const map = useAtomValue(mapInstanceAtom);
  const viewState = useAtomValue(mapViewStateAtom);

  const bgColor = chroma(feature.properties?.fillColor ?? "#ffffff");
  const textColor = bgColor.luminance() > 0.5 ? "black" : "white";

  const labels: BoundaryLabel[] = useMemo(() => {
    if (!map) return [];
    const showEndCaps = feature.properties?.displayEndCaps === "on";
    const showDistance = feature.properties?.displayDistance === "on";
    const line = turf.lineString(feature.geometry.coordinates);
    const distanceInMiles = turf.length(line, { units: "miles" });
    const midpoint = turf.along(line, turf.length(line) / 2);
    const startPoint = line.geometry.coordinates[0];
    const endPoint =
      line.geometry.coordinates[line.geometry.coordinates.length - 1];

    const startPixel = map.project(startPoint as mapboxgl.LngLatLike);
    const endPixel = map.project(endPoint as mapboxgl.LngLatLike);

    const pixelDistance = Math.sqrt(
      Math.pow(endPixel.x - startPixel.x, 2) +
        Math.pow(endPixel.y - startPixel.y, 2)
    );

    let distance, unit;
    if (distanceInMiles < 0.1) {
      distance = (distanceInMiles * 5280).toFixed(0);
      unit = "ft";
    } else {
      distance = distanceInMiles.toFixed(2);
      unit = "mi";
    }

    if (pixelDistance < MIN_PIXEL_DISTANCE) {
      return [];
    }

    return [
      ...(showDistance
        ? [
            {
              text: `${distance} ${unit}`,
              position: midpoint.geometry.coordinates,
              offset: [0, 0] as [number, number],
            },
          ]
        : []),
      ...(showEndCaps
        ? [
            {
              text: "Start",
              icon: <IconPlayerPlayFilled size={10} />,
              position: startPoint,
              offset: [0, 20] as [number, number],
            },
            {
              text: "End",
              icon: <IconPlayerStopFilled size={10} />,
              position: endPoint,
              offset: [0, 20] as [number, number],
            },
          ]
        : []),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, feature, viewState.zoom]);

  return (
    <>
      {labels.map((label, index) => (
        <Marker
          key={index}
          longitude={label.position[0]}
          latitude={label.position[1]}
          offset={label.offset}
        >
          <div
            className="px-2 py-0.5 rounded-full font-medium flex items-center gap-1"
            style={
              {
                backgroundColor: feature.properties?.fillColor ?? "white",
                color: textColor,
              } as React.CSSProperties
            }
          >
            {label.icon}
            {label.text}
          </div>
        </Marker>
      ))}
    </>
  );
}
